/**
 * Represents a chunked segment of a sequence of values.
 *
 * @template T - The type of values in the chunk.
 */
export interface Chunk<T> {
  /**
   * The zero-based index of the chunk.
   */
  chunkIndex: number;

  /**
   * The zero-based index in the original
   * sequence of the first value in the chunk.
   */
  startIndex: number;

  /**
   * The values contained in the chunk.
   */
  values: T[];
}

/**
 * Asynchronously splits an iterable into fixed-size chunks.
 *
 * @template T - The type of values in the input iterable.
 *
 * @param size - The maximum number of items per chunk. Must be a positive integer or `Infinity`.
 * @param values - The source of values to be chunked.
 *
 * @yields {Chunk<T>} An object representing the next chunk in the sequence.
 *
 * @throws {RangeError} If `size` is less than 1 or not an integer (except for `Infinity`).
 *
 * @example
 * const data = [1, 2, 3, 4, 5];
 * for await (const chunk of chunkify(2, data)) {
 *   console.log(chunk);
 * }
 * // Output:
 * // { chunkIndex: 0, startIndex: 0, values: [1, 2] }
 * // { chunkIndex: 1, startIndex: 2, values: [3, 4] }
 * // { chunkIndex: 2, startIndex: 4, values: [5] }
 */
export async function* asyncChunkify<T>(
  size: number,
  values: Iterable<T> | AsyncIterable<T>,
): AsyncGenerator<Chunk<T>> {
  if (size !== Infinity && (!Number.isInteger(size) || size < 1)) {
    throw new RangeError("Invalid chunk length");
  }

  let chunkIndex = 0;
  let startIndex = 0;
  let chunk: T[] = [];
  for await (const value of values) {
    if (chunk.push(value) >= size) {
      yield { chunkIndex, startIndex, values: chunk };
      chunk = [];
      ++chunkIndex;
      startIndex += chunk.length;
    }
  }

  if (chunk.length > 0) {
    yield { chunkIndex, startIndex, values: chunk };
  }
}

/**
 * Splits an iterable into fixed-size chunks.
 *
 * @template T - The type of values in the input iterable.
 *
 * @param size - The maximum number of items per chunk. Must be a positive integer or `Infinity`.
 * @param values - The source of values to be chunked.
 *
 * @yields {Chunk<T>} An object representing the next chunk in the sequence.
 *
 * @throws {RangeError} If `size` is less than 1 or not an integer (except for `Infinity`).
 *
 * @example
 * const data = [1, 2, 3, 4, 5];
 * for (const chunk of chunkify(2, data)) {
 *   console.log(chunk);
 * }
 * // Output:
 * // { chunkIndex: 0, startIndex: 0, values: [1, 2] }
 * // { chunkIndex: 1, startIndex: 2, values: [3, 4] }
 * // { chunkIndex: 2, startIndex: 4, values: [5] }
 */
export function* chunkify<T>(
  size: number,
  values: Iterable<T>,
): Generator<Chunk<T>> {
  if (size !== Infinity && (!Number.isInteger(size) || size < 1)) {
    throw new RangeError("Invalid chunk length");
  }

  let chunkIndex = 0;
  let startIndex = 0;
  let chunk: T[] = [];
  for (const value of values) {
    if (chunk.push(value) >= size) {
      yield { chunkIndex, startIndex, values: chunk };
      chunk = [];
      ++chunkIndex;
      startIndex += chunk.length;
    }
  }

  if (chunk.length > 0) {
    yield { chunkIndex, startIndex, values: chunk };
  }
}
